/**
 * Email addresses.
 *
 * Added 29 Jul 2026 at the owner's direction, alongside password reset. The
 * email is now a SECOND login identity — a member signs in with their mobile
 * number OR their email, plus the same password either way — and it is the
 * only channel a forgotten password can be recovered through. That makes it
 * mandatory on every form that creates a member (SPEC.md §4, CLAUDE.md §10.6
 * reasoning applied to email).
 *
 * Same discipline as phone.js: normalise on write, one stored shape, and a
 * lookup that agrees with however the member typed it.
 */

/**
 * Deliberately not RFC 5322. That grammar accepts addresses no mail provider
 * will ever route, and rejecting `a@b` at the form is worth more to this club
 * than accepting `"weird name"@[192.168.0.1]`.
 */
const SHAPE = /^[^\s@,;]+@[^\s@,;.]+(\.[^\s@,;.]+)+$/;

/**
 * Lowercases and trims. Returns null when it cannot be an email address, so
 * the caller shows a validation message instead of silently storing rubbish.
 *
 * The local part is lowercased too. That is technically lossy — RFC says the
 * left of the @ is case-sensitive — but no consumer mail provider honours
 * that, and a member who typed `Priya@` on the join form and `priya@` on the
 * reset form must land on the same account.
 */
export function normalizeEmail(input) {
  if (!input) return null;
  const trimmed = String(input).trim().toLowerCase();
  if (!trimmed || trimmed.length > 254) return null;
  return SHAPE.test(trimmed) ? trimmed : null;
}

/** Whether a typed string is an attempt at an email rather than a number. */
export function looksLikeEmail(input) {
  return typeof input === 'string' && input.includes('@');
}

/** Two addresses are the same inbox if they normalise to the same string. */
export function sameEmail(a, b) {
  const left = normalizeEmail(a);
  const right = normalizeEmail(b);
  return left !== null && left === right;
}

/**
 * The member behind an address, preferring one still on the roster — the same
 * rule findMemberByPhone follows, and for the same reason: a rejoined member
 * can have an old `left` record, and matching it would lock them out of the
 * membership they just paid for.
 */
export function findMemberByEmail(members, email) {
  const normalized = normalizeEmail(email);
  if (!normalized) return null;
  const matches = members.filter((m) => normalizeEmail(m.email) === normalized);
  return matches.find((m) => m.status !== 'left') ?? matches[0] ?? null;
}

/**
 * Whether an address already belongs to someone on the live roster.
 * `exceptId` lets an edit form leave a member's own address alone.
 */
export function emailTaken(members, email, exceptId = null) {
  const found = findMemberByEmail(members, email);
  return Boolean(found && found.status !== 'left' && found.id !== exceptId);
}

/**
 * `lakshmi.priya@gmail.com` → `la••••••ya@gmail.com`.
 *
 * Shown after a password reset is requested, so the member can tell WHICH
 * inbox to open — most people here have a work address and a personal one —
 * without the full address being readable over their shoulder, or harvestable
 * by anyone who guesses a club mobile number.
 *
 * The domain stays in the clear on purpose: "check your Gmail" is the useful
 * half of the hint, and it identifies nobody.
 */
export function maskEmail(email) {
  const normalized = normalizeEmail(email);
  if (!normalized) return '—';

  const at = normalized.lastIndexOf('@');
  const local = normalized.slice(0, at);
  const domain = normalized.slice(at); // includes the @

  // Short local parts have nothing to hide behind: show the first character
  // only, never the last, or `ab@x.com` would be printed in full.
  if (local.length <= 4) return `${local[0]}${'•'.repeat(Math.max(2, local.length - 1))}${domain}`;

  return `${local.slice(0, 2)}${'•'.repeat(local.length - 4)}${local.slice(-2)}${domain}`;
}
